import { TourOfferDeparture } from '~/types/tours/tourOffer';

// Bundles sell single and double: one single space, or two twin-share spaces.
const SPACES_NEEDED: ReadonlyArray<{ roomType: string; spaces: number }> = [
  { roomType: 'single', spaces: 1 },
  { roomType: 'twin', spaces: 2 },
];

export function spacesLeft(departure: TourOfferDeparture | undefined, roomType: string): number {
  return departure?.roomAvailability.find((room) => room.roomType === roomType)?.spacesLeft ?? 0;
}

/** Whether the tour can take both a single and a double on this departure. */
export function isTourAvailable(departure: TourOfferDeparture | undefined): boolean {
  return SPACES_NEEDED.every(({ roomType, spaces }) => spacesLeft(departure, roomType) >= spaces);
}

const BUNDLE_CURRENCY = 'AUD';

const roomTypeFor = (occupancy: number) => (occupancy === 1 ? 'single' : 'twin');

/** A tour configuration and one of its packages, by name as svc-tour prices them. */
export interface TourOption {
  tourConfiguration: string;
  packageConfiguration: string;
}

export const tourOptionKey = (option: TourOption) =>
  JSON.stringify([option.tourConfiguration.toLowerCase(), option.packageConfiguration.toLowerCase()]);

/** Every option priced in AUD on any of the departures. */
export function tourOptions(departures: Array<TourOfferDeparture>): Array<TourOption> {
  const options = new Map<string, TourOption>();
  departures.forEach((departure) =>
    departure.configurationPrices
      .filter((price) => price.currency === BUNDLE_CURRENCY)
      .forEach(({ tourConfiguration, packageConfiguration }) =>
        options.set(tourOptionKey({ tourConfiguration, packageConfiguration }), {
          tourConfiguration,
          packageConfiguration,
        }),
      ),
  );
  return [...options.values()];
}

/**
 * The tour's price for one occupancy on the chosen option, or on whichever option is cheapest
 * for that room when none is chosen. Undefined when that option isn't priced on the departure.
 */
export function tourOptionPrice(
  departure: TourOfferDeparture | undefined,
  occupancy: number,
  option: TourOption | undefined,
): { price: number; option: TourOption } | undefined {
  const roomType = roomTypeFor(occupancy);
  const candidates = (departure?.configurationPrices ?? []).filter(
    (price) =>
      price.roomType === roomType &&
      price.currency === BUNDLE_CURRENCY &&
      (!option || tourOptionKey(price) === tourOptionKey(option)),
  );
  if (candidates.length === 0) {
    return undefined;
  }
  const cheapest = candidates.reduce((best, price) => (price.price < best.price ? price : best));
  return {
    price: cheapest.price * occupancy,
    option: { tourConfiguration: cheapest.tourConfiguration, packageConfiguration: cheapest.packageConfiguration },
  };
}
